/**
 * Knowledge → Bulk add (9 Oct): the main point of v1. "We should be able to
 * bulk load knowledge" (product owner) — no approver, no grant.
 *
 * Drop many files at once (.md with front matter, .docx, .pptx, .pdf, .txt,
 * .html, a .xlsx/.csv sheet with one row per article, or a .zip of those,
 * such as the knowledge seed with its manifest.csv). The server reads them —
 * nothing is saved — and the table shows one row per article with its
 * duplicate check, suggested areas and warnings. Then one click: "Add as
 * marked" (each row's status), "Add as drafts" or "Add and publish all".
 * Large selections go up in several requests; a large batch indexes in the
 * background and the page shows its progress.
 */

import { FileUp, Icon } from "@norican/icons";
import {
  Alert,
  Badge,
  Button,
  cn,
  DataGrid,
  type DataGridColumn,
  Progress,
  Typography,
} from "@norican/react";
import { useMemo, useRef, useState } from "react";
import { Link } from "react-router";

import { AppBreadcrumbs, ErrorState, PageContainer, SectionCard } from "@/components/common";
import { useDocumentTitle } from "@/hooks";
import { useT } from "@/i18n";
import type { KnowledgeImportItem, KnowledgePreviewItem, KnowledgePreviewProblem } from "@/lib/api";
import { logger } from "@/lib/logger";

import {
  fileToBase64,
  previewKnowledgeFiles,
  useCommitKnowledgeImport,
  useKnowledgeAreas,
  useKnowledgeBatch,
} from "./api";
import { areaNames, isBulkFile, shortFileName, uploadBatches } from "./lib/knowledge";
import { KnowledgeGate, OptionSelect } from "./shared";

/** Raw bytes per preview request: base64 adds a third, under the server's 16 MB. */
const BATCH_BYTES = 11 * 1024 * 1024;

type Action = "add" | "update" | "skip";
type Status = "draft" | "published" | "retired";

interface Row extends KnowledgePreviewItem {
  action: Action;
}

export function KnowledgeBulkAddPage() {
  const { t } = useT();
  useDocumentTitle(t("knowledgeAdmin.bulk.title"));
  return (
    <KnowledgeGate title={t("knowledgeAdmin.bulk.title")} editorsOnly>
      <BulkBody />
    </KnowledgeGate>
  );
}

function BulkBody() {
  const { t } = useT();
  const input = useRef<HTMLInputElement>(null);
  const areas = useKnowledgeAreas();
  const commit = useCommitKnowledgeImport();
  const [dragging, setDragging] = useState(false);
  const [reading, setReading] = useState<{ done: number; total: number } | null>(null);
  const [readError, setReadError] = useState<unknown>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [problems, setProblems] = useState<KnowledgePreviewProblem[]>([]);
  const [fileNames, setFileNames] = useState<string[]>([]);
  const [batchId, setBatchId] = useState<string | null>(null);
  const batch = useKnowledgeBatch(batchId);

  async function read(files: File[]) {
    const usable = files.filter((file) => isBulkFile(file.name));
    const skipped = files
      .filter((file) => !isBulkFile(file.name))
      .map((file) => ({ file_name: file.name, error: t("knowledgeAdmin.bulk.unsupported") }));
    if (usable.length === 0) {
      setProblems(skipped);
      return;
    }
    setBatchId(null);
    commit.reset();
    setReadError(null);
    setRows([]);
    setProblems(skipped);
    setFileNames(usable.map((file) => file.name));
    const batches = uploadBatches(usable, BATCH_BYTES);
    setReading({ done: 0, total: usable.length });
    let done = 0;
    try {
      for (const [index, group] of batches.entries()) {
        const payload = await Promise.all(
          group.map(async (file) => ({
            name: file.name,
            content_base64: await fileToBase64(file),
          })),
        );
        const result = await previewKnowledgeFiles(payload);
        setRows((current) => [
          ...current,
          ...result.items.map((item) => ({
            ...item,
            key: `${index}-${item.key}`,
            action: item.suggested_action,
          })),
        ]);
        setProblems((current) => [...current, ...result.problems]);
        done += group.length;
        setReading({ done, total: usable.length });
      }
    } catch (error) {
      logger.warn("[knowledge] preview failed", error);
      setReadError(error);
    } finally {
      setReading(null);
    }
  }

  function change(key: string, patch: Partial<Row>) {
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  }

  function save(mode: "draft" | "publish" | "as_marked") {
    const items: KnowledgeImportItem[] = rows.map((row) => ({
      file_name: row.file_name,
      title: row.title,
      summary: row.summary,
      body_markdown: row.body_markdown,
      tags: row.tags,
      areas: row.areas,
      audience: row.audience,
      owner: row.owner,
      status: row.status,
      review_due: row.review_due,
      source_type: row.source_type,
      source_ref: row.source_ref,
      external_id: row.external_id,
      language: row.language,
      action: row.action,
      target_id: row.target_id,
    }));
    commit.mutate(
      { mode, file_names: fileNames, items },
      { onSuccess: (result) => setBatchId(result.id) },
    );
  }

  const areaList = useMemo(() => areas.data?.items ?? [], [areas.data]);
  const columns = useMemo<DataGridColumn<Row>[]>(
    () => [
      {
        id: "title",
        header: t("knowledgeAdmin.field.title"),
        width: 340,
        accessor: (row) => row.title,
        cell: (row) => (
          <span className="flex flex-col gap-0.5">
            <span className={cn("font-medium", row.action === "skip" && "text-fg-muted")}>
              {row.title}
            </span>
            <span className="text-fg-muted text-xs" title={row.file_name ?? ""}>
              {shortFileName(row.file_name ?? "")} ·{" "}
              {t("knowledgeAdmin.bulk.words", { count: row.words })}
            </span>
            {(row.warnings ?? []).length > 0 ? (
              <span className="text-warning-fg text-xs">{(row.warnings ?? []).join(" · ")}</span>
            ) : null}
          </span>
        ),
      },
      {
        id: "areas",
        header: t("knowledgeAdmin.field.areas"),
        width: 200,
        accessor: (row) => areaNames(row.areas ?? [], areaList).join(", "),
        cell: (row) => (
          <span className="text-sm">
            {areaNames(row.areas ?? [], areaList).join(", ") || (
              <span className="text-fg-muted">{t("knowledgeAdmin.areas.none")}</span>
            )}
          </span>
        ),
      },
      {
        id: "duplicate",
        header: t("knowledgeAdmin.bulk.duplicate"),
        width: 190,
        accessor: (row) => row.duplicate?.kind ?? "",
        cell: (row) =>
          row.duplicate ? (
            <span className="flex flex-col gap-0.5">
              <Badge tone="warning" variant="subtle" size="sm">
                {t(`knowledgeAdmin.bulk.dup.${row.duplicate.kind}`)}
              </Badge>
              {row.duplicate.article_id ? (
                <Link
                  to={`/knowledge/articles/${row.duplicate.article_id}`}
                  className="text-accent-fg-subtle text-xs hover:underline"
                >
                  {row.duplicate.article_id}
                </Link>
              ) : null}
            </span>
          ) : (
            <span className="text-fg-muted text-sm">{t("knowledgeAdmin.bulk.new")}</span>
          ),
      },
      {
        id: "status",
        header: t("knowledgeAdmin.field.status"),
        width: 150,
        accessor: (row) => row.status,
        cell: (row) => (
          <OptionSelect
            options={(["draft", "published"] as const).map((value) => ({
              value,
              label: t(`knowledgeAdmin.status.${value}`),
            }))}
            value={row.status === "retired" ? "draft" : (row.status ?? "draft")}
            onValueChange={(v) => change(row.key, { status: (v || "draft") as Status })}
            placeholder=""
            aria-label={t("knowledgeAdmin.field.status")}
          />
        ),
      },
      {
        id: "action",
        header: t("knowledgeAdmin.bulk.action"),
        width: 160,
        pinned: "right",
        accessor: (row) => row.action,
        cell: (row) => (
          <OptionSelect
            options={[
              { value: "add", label: t("knowledgeAdmin.bulk.actionAdd") },
              ...(row.target_id
                ? [{ value: "update", label: t("knowledgeAdmin.bulk.actionUpdate") }]
                : []),
              { value: "skip", label: t("knowledgeAdmin.bulk.actionSkip") },
            ]}
            value={row.action}
            onValueChange={(v) => change(row.key, { action: (v || "skip") as Action })}
            placeholder=""
            aria-label={t("knowledgeAdmin.bulk.action")}
          />
        ),
      },
    ],
    [areaList, t],
  );

  const counted = rows.filter((row) => row.action !== "skip").length;
  const marked = rows.filter((row) => row.action !== "skip" && row.status === "published").length;
  const result = batch.data ?? commit.data;

  return (
    <PageContainer
      title={t("knowledgeAdmin.bulk.title")}
      subtitle={t("knowledgeAdmin.bulk.subtitle")}
      breadcrumbs={
        <AppBreadcrumbs
          items={[
            { label: t("knowledgeAdmin.overview.title"), to: "/knowledge" },
            { label: t("knowledgeAdmin.bulk.title") },
          ]}
        />
      }
      width="wide"
    >
      <div className="flex flex-col gap-4">
        <div
          role="button"
          tabIndex={0}
          aria-label={t("knowledgeAdmin.bulk.drop")}
          onClick={() => input.current?.click()}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              input.current?.click();
            }
          }}
          onDragOver={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => {
            event.preventDefault();
            setDragging(false);
            void read(Array.from(event.dataTransfer.files));
          }}
          className={cn(
            "border-border flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed p-8 text-center",
            dragging && "border-accent bg-accent-subtle",
          )}
        >
          <Icon icon={FileUp} size="lg" tone="accent" />
          <Typography variant="body" className="font-medium">
            {t("knowledgeAdmin.bulk.drop")}
          </Typography>
          <Typography variant="body-sm" tone="muted">
            {t("knowledgeAdmin.bulk.types")}
          </Typography>
          <input
            ref={input}
            type="file"
            multiple
            className="hidden"
            accept=".md,.markdown,.txt,.html,.htm,.docx,.pptx,.pdf,.xlsx,.csv,.zip"
            onChange={(event) => {
              const files = Array.from(event.target.files ?? []);
              event.target.value = "";
              void read(files);
            }}
          />
        </div>

        {reading ? (
          <Progress.Root value={(reading.done / Math.max(reading.total, 1)) * 100}>
            <Progress.Label>
              {t("knowledgeAdmin.bulk.reading", { done: reading.done, total: reading.total })}
            </Progress.Label>
            <Progress.Track>
              <Progress.Indicator />
            </Progress.Track>
          </Progress.Root>
        ) : null}
        {readError == null ? null : <ErrorState error={readError} variant="inline" />}

        {problems.length > 0 ? (
          <SectionCard
            title={t("knowledgeAdmin.bulk.problems", { count: problems.length })}
            padding="sm"
          >
            <ul className="flex list-none flex-col gap-1 p-0 text-sm">
              {problems.map((problem, index) => (
                <li
                  key={`${problem.file_name}-${index}`}
                  className={problem.skipped ? "text-fg-muted" : "text-danger-fg"}
                >
                  <span className="font-medium">{problem.file_name}</span>: {problem.error}
                </li>
              ))}
            </ul>
          </SectionCard>
        ) : null}

        {rows.length > 0 && result === undefined ? (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <Typography variant="body-sm">
                {t("knowledgeAdmin.bulk.found", { count: rows.length, adding: counted })}
              </Typography>
              <div className="ml-auto flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  disabled={counted === 0 || reading !== null}
                  loading={commit.isPending}
                  onClick={() => save("draft")}
                >
                  {t("knowledgeAdmin.bulk.addDrafts")}
                </Button>
                <Button
                  variant="outline"
                  disabled={counted === 0 || reading !== null}
                  loading={commit.isPending}
                  onClick={() => save("publish")}
                >
                  {t("knowledgeAdmin.bulk.addPublish")}
                </Button>
                <Button
                  disabled={counted === 0 || reading !== null}
                  loading={commit.isPending}
                  onClick={() => save("as_marked")}
                >
                  {t("knowledgeAdmin.bulk.addMarked", { count: marked })}
                </Button>
              </div>
            </div>
            {commit.error == null ? null : <ErrorState error={commit.error} variant="inline" />}
            <DataGrid
              aria-label={t("knowledgeAdmin.bulk.title")}
              columns={columns}
              data={rows}
              getRowId={(row) => row.key}
              tableLayout="fixed"
              density="compact"
            />
          </>
        ) : null}

        {result ? (
          <Alert
            tone={result.result === "done_with_errors" ? "warning" : "success"}
            title={t("knowledgeAdmin.bulk.doneTitle")}
          >
            <div className="flex flex-col gap-2">
              <span>
                {t("knowledgeAdmin.bulk.doneCounts", {
                  added: result.counts.added,
                  updated: result.counts.updated,
                  skipped: result.counts.skipped,
                  published: result.counts.published,
                  failed: result.counts.failed,
                })}
              </span>
              {result.index_total > 0 ? (
                <Progress.Root
                  value={
                    ((result.index_done + result.index_failed) / Math.max(result.index_total, 1)) *
                    100
                  }
                >
                  <Progress.Label>
                    {t("knowledgeAdmin.bulk.indexing", {
                      done: result.index_done,
                      failed: result.index_failed,
                      total: result.index_total,
                    })}
                  </Progress.Label>
                  <Progress.Track>
                    <Progress.Indicator />
                  </Progress.Track>
                </Progress.Root>
              ) : null}
              {result.errors.map((error, index) => (
                <span key={index} className="text-sm">
                  {error.title}: {error.error}
                </span>
              ))}
              <div className="flex flex-wrap gap-2">
                <Button size="sm" render={<Link to="/knowledge/articles" />}>
                  {t("knowledgeAdmin.bulk.seeArticles")}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setRows([]);
                    setProblems([]);
                    setBatchId(null);
                    commit.reset();
                  }}
                >
                  {t("knowledgeAdmin.bulk.again")}
                </Button>
              </div>
            </div>
          </Alert>
        ) : null}
      </div>
    </PageContainer>
  );
}
